/**
 * @file   src\Pages\MyWallet.tsx
 * @brief  My Wallet page.
 * @date   SEP, 2026
 * @author ZCO Engineer
 * @copyright (c) 2026, ZCO
 */

import { React, Link, useEffect, useState, useParams, useNavigate, Button } from "../utils/thirdpartyComponents";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import Gobackicon from "../assets/images/left-arrow-small.svg";
import Giftpattern from "../assets/images/giftpattern.svg";
import CWInputGroup from "src/components/CWInputGroup";
import "src/styles/inner-page-styles.scss";
import { pageURLs } from "src/utils/pageUrls";
import { MessageToaster } from "src/utils/toastUtil";
import axios from "src/config/axiosConfig";
import { GiftCardApi } from "src/utils/apiUrls";
import { formatMoneyParts, getCurrencySymbol } from "src/utils/helper";
import SmallLoader from "src/components/SmallLoader";
import GiftCardClaimOtp from "src/components/GiftCardClaimOtp";

const GIFT_CARD_SUCCESS = 1000;
const CLAIM_OTP_LOCKED = 1046;

interface WalletGiftCard {
  id: string;
  giftCardCode: string;
  giftCardNumber: string;
  giftCardInstanceId?: number;
  name: string;
  faceValue: number;
  purchasePrice: number;
  balance: number;
  status: number;
  currency: string;
  createdOn: string;
}

// Temporarily hidden with the Loyalty Points tab.
// const LOYALTY_ACTIVITIES = [
//   { activity: "New User Sign-Up", points: 50 },
//   { activity: "Single Wash Completed", points: 75 },
//   { activity: "Subscription Purchased", points: 75 },
//   { activity: "Subscription Renewal", points: 100 },
//   { activity: "Gift Card Purchase (Optional)", points: 75 },
// ];


const GIFT_CARD_STATUS: Record<number, string> = {
  1: "Pending Claim",
  2: "Active",
  4: "Void",
};

const toNumber = (value: unknown, fallback = 0) => {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : fallback;
};

const getCardNumberGroups = (value: string) => {
  const cleaned = String(value || "").replace(/[^a-zA-Z0-9]/g, "");
  return cleaned.match(/.{1,4}/g) ?? [];
};

const mapWalletCard = (item: any, index: number): WalletGiftCard => {
  const giftCardCode = String(item?.giftCardCode ?? "");
  const giftCardInstanceId =
    item?.giftCardInstanceId != null && item?.giftCardInstanceId !== ""
      ? toNumber(item.giftCardInstanceId)
      : undefined;

  return {
    id: giftCardCode || (giftCardInstanceId != null ? String(giftCardInstanceId) : String(index)),
    giftCardCode,
    giftCardNumber: String(item?.giftCardNumber ?? ""),
    giftCardInstanceId,
    name: item?.name || "Gift Card",
    faceValue: toNumber(item?.faceValue),
    purchasePrice: toNumber(item?.purchasePrice),
    balance: toNumber(item?.balance),
    status: toNumber(item?.status),
    currency: item?.currency || "",
    createdOn: item?.createdOn || "",
  };
};

const MyWallet = () => {
  const params = useParams();
  const navigate = useNavigate();
  const toaster = new MessageToaster();
  const [giftCardId, setGiftCardId] = useState("");
  const [giftCardError, setGiftCardError] = useState("");
  const [giftCards, setGiftCards] = useState<WalletGiftCard[]>([]);
  const [loading, setLoading] = useState(false);
  const [showOtp, setShowOtp] = useState(false);
  const [claimCode, setClaimCode] = useState("");

  useEffect(() => {
    fetchWallet();
  }, []);

  const fetchWallet = async () => {
    const locationId = Number(localStorage.getItem("selectedLocation") || 0);
    if (!locationId) {
      toaster.toastError("Select a location.");
      return;
    }

    try {
      setLoading(true);
      const { data } = await axios.get(GiftCardApi.WALLET, {
        params: { locationId },
      });
      if (!data) {
        return;
      }
      const list = Array.isArray(data?.data?.list)
        ? data.data.list
        : Array.isArray(data?.responseBody?.list)
          ? data.responseBody.list
          : null;
      if ((data.error === 0 || data.statusCode === GIFT_CARD_SUCCESS) && list) {
        setGiftCards(list.map(mapWalletCard));
      } else {
        setGiftCards([]);
        toaster.toastError(data?.message || "Unable to load gift cards.");
      }
    } catch (error: any) {
      setGiftCards([]);
      toaster.toastError(
        error?.data?.message || error?.response?.data?.message || error?.message || "Unable to load gift cards."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleValidate = async () => {
    const giftCardCode = giftCardId.trim();
    if (!giftCardCode) {
      setGiftCardError("Enter a gift card ID.");
      return;
    }
    const locationId = Number(localStorage.getItem("selectedLocation") || 0);
    if (!locationId) {
      toaster.toastError("Select a location.");
      return;
    }

    try {
      setGiftCardError("");
      setLoading(true);
      const { data } = await axios.post(GiftCardApi.CLAIM_START, {
        locationId,
        giftCardNumber: giftCardCode,
      });
      if (!data) {
        return;
      }
      if (data.error === 0 || data.statusCode === GIFT_CARD_SUCCESS) {
        setClaimCode(giftCardCode);
        setShowOtp(true);
        return;
      }
      const status = Number(data?.statusCode ?? data?.error ?? 0);
      if (status === CLAIM_OTP_LOCKED) {
        toaster.toastError(data?.message || "Verification is locked. Try again later.");
      } else {
        toaster.toastError(data?.message || "Unable to send the verification code.");
      }
    } catch (error: any) {
      toaster.toastError(
        error?.data?.message || error?.response?.data?.message || error?.message || "Unable to send the verification code."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClaimed = () => {
    setGiftCardId("");
    fetchWallet();
  };

  const handleBuyGiftCard = () => {
    navigate("/" + params.carwashname + "/" + pageURLs.buy_gift_card);
  };

  return (
    <div className="common-content-wrap wallet-page pb-115">
      <div className="inner-content-wrap">
        <div className="common-header two-block-header">
          <div className="pe-4 ps-4 pt-0 pb-0 h-100">
            <div className="common-header-inner">
              <Link
                to={"/" + params.carwashname + "/" + pageURLs.settings}
                className="back-link"
              >
                <img src={Gobackicon} alt="Car Wash App" />
              </Link>
              My Wallet
            </div>
          </div>
        </div>

        <Tabs defaultActiveKey="giftcard" id="wallet-tabs" className="buy-wash-tabwrap">
          <Tab eventKey="giftcard" title="Gift Card">
            <div className="payment-content-sec">
              <div className="amount-info">
                <h6>Receive Gift Card</h6>
                <p>Enter the gift card number you received in your email address.</p>
              </div>
              <div className="promo-input position-relative">
                <CWInputGroup
                  id="giftCardId"
                  name="giftCardId"
                  placeholder="Gift Card Number"
                  type="text"
                  value={giftCardId}
                  errorMessage={giftCardError}
                  showLabel={false}
                  onChange={(event) => {
                    setGiftCardId(event.target.value);
                    if (giftCardError) {
                      setGiftCardError("");
                    }
                  }}
                />
                <Button
                  variant="primary"
                  className="primary-btn promo-btn"
                  onClick={handleValidate}
                >
                  Validate
                </Button>
              </div>

              <div className="amount-info">
                <h6>Your Gift Cards</h6>
              </div>
              <div className="wallet-gift-list">
                {!loading && giftCards.length === 0 ? <p>No gift card found</p> : null}
                {giftCards.map((card) => {
                  const faceValue = formatMoneyParts(card.faceValue, card.currency);
                  const balance = formatMoneyParts(card.balance, card.currency);
                  const symbol = getCurrencySymbol(card.currency);
                  const statusLabel = GIFT_CARD_STATUS[card.status];
                  return (
                    <div className="wallet-gift-card" key={card.id}>
                      <div className="wallet-gift-card-pattern" aria-hidden="true">
                      <img src={Giftpattern} alt="Car Wash App" />
                      </div>
                      <p className="wallet-gift-card-name">{card.name}</p>
                      {card.giftCardNumber ? (
                        <p className="wallet-gift-card-number">
                          {getCardNumberGroups(card.giftCardNumber).map((group, index) => (
                            <span key={`${card.id}-${index}`}>{group}</span>
                          ))}
                        </p>
                      ) : null}

                      <div className="wallet-gift-card-bottom">
                        <div className="gift-price-block">
                          <span className="gift-price-label">Purchase value</span>
                          <span className="cw-card-value">
                            {symbol}
                            {faceValue.dollars}
                            <span className="decimal-sec">.{faceValue.cents}</span>
                          </span>
                        </div>
                        <div className="gift-price-block gift-price-block-end">
                          <span className="gift-price-label">Balance</span>
                          <span className="cw-card-value">
                            {symbol}
                            {balance.dollars}
                            <span className="decimal-sec">.{balance.cents}</span>
                          </span>
                          {statusLabel && card.status !== 2 ? (
                            <span className="gift-price-label">{statusLabel}</span>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="btm-wrap">
              <div className="btm-inner-wrap">
                <Button
                  variant="primary"
                  className="primary-btn btn-main"
                  onClick={handleBuyGiftCard}
                >
                  Buy Gift Card
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20">
  <path id="gift-svgrepo-com_1_" data-name="gift-svgrepo-com (1)" d="M8.464,2A4.03,4.03,0,0,1,12,4.272,4.03,4.03,0,0,1,15.536,2a2.966,2.966,0,0,1,2.1.885A3,3,0,0,1,18.5,5a3.035,3.035,0,0,1-.168,1H19.4a3.146,3.146,0,0,1,1.508.218,1.988,1.988,0,0,1,.874.874A3.145,3.145,0,0,1,22,8.6v1.8a3.145,3.145,0,0,1-.218,1.508,1.988,1.988,0,0,1-.874.874,2.129,2.129,0,0,1-.908.209V17.8a5.5,5.5,0,0,1-.327,2.562,2.981,2.981,0,0,1-1.311,1.311A5.5,5.5,0,0,1,15.8,22H8.2a5.5,5.5,0,0,1-2.562-.327,2.981,2.981,0,0,1-1.311-1.311A5.5,5.5,0,0,1,4,17.8V12.991a2.129,2.129,0,0,1-.908-.209,1.988,1.988,0,0,1-.874-.874A3.146,3.146,0,0,1,2,10.4V8.6a3.146,3.146,0,0,1,.218-1.508,1.988,1.988,0,0,1,.874-.874A3.146,3.146,0,0,1,4.6,6H5.668A3.036,3.036,0,0,1,5.5,5a3,3,0,0,1,.862-2.115A2.966,2.966,0,0,1,8.464,2Zm2.165,4c-.383-.907-1.072-2-2.165-2a.941.941,0,0,0-.675.286,1.026,1.026,0,0,0,0,1.427A.941.941,0,0,0,8.464,6Zm4.907,0a.941.941,0,0,0,.676-.287,1.026,1.026,0,0,0,0-1.427A.941.941,0,0,0,15.536,4c-1.1,0-1.79,1.093-2.17,2ZM8.464,8H4.6c-.231,0-.468,0-.589.011C4,8.132,4,8.369,4,8.6v1.8c0,.231,0,.468.011.589C4.132,11,4.369,11,4.6,11H19.4c.231,0,.468,0,.589-.011C20,10.868,20,10.631,20,10.4V8.6c0-.231,0-.468-.011-.589C19.868,8,19.631,8,19.4,8ZM18,13H6v4.8a5.557,5.557,0,0,0,.109,1.654.994.994,0,0,0,.437.437A5.555,5.555,0,0,0,8.2,20h7.6a5.557,5.557,0,0,0,1.654-.109.993.993,0,0,0,.437-.437A5.555,5.555,0,0,0,18,17.8Z" transform="translate(-2 -2)" fill="#172951"/>
</svg>

                </Button>
              </div>
            </div>
          </Tab>
          {/* Temporarily hidden
          <Tab eventKey="loyalty" title="Loyalty Points">
            <div className="payment-content-sec loyalty-tab">
              <div className="amount-info">
                <h6>Available Loyalty Points</h6>
                <p>View your earned points and redeem them for rewards.</p>
              </div>
              <div className="total-amount-feed loyalty-points-banner d-flex align-items-center">
                <span className="loyalty-star" aria-hidden="true">
                <svg xmlns="http://www.w3.org/2000/svg" width="23.999" height="28.498" viewBox="0 0 23.999 28.498">
  <g id="stars-1-svgrepo-com" transform="translate(-4 -2)">
    <path id="Path_726" data-name="Path 726" d="M18.877,11.533c-1.48-1.771-2.22-2.656-3.07-2.52s-1.248,1.2-2.044,3.34l-.206.552A3.143,3.143,0,0,1,13,14.04a3.153,3.153,0,0,1-1.126.579l-.55.215c-2.126.833-3.189,1.249-3.31,2.1s.78,1.573,2.581,3.017l.466.374a3.273,3.273,0,0,1,.916.9,3.218,3.218,0,0,1,.219,1.255l.043.591c.166,2.285.249,3.428,1.025,3.817s1.73-.232,3.64-1.475l.494-.322a3.159,3.159,0,0,1,1.125-.58,3.234,3.234,0,0,1,1.261.2l.577.15c2.228.58,3.343.87,3.943.259s.29-1.716-.332-3.929l-.161-.572a3.213,3.213,0,0,1-.22-1.255,3.144,3.144,0,0,1,.561-1.133l.313-.5c1.211-1.927,1.817-2.89,1.412-3.657s-1.551-.829-3.844-.953l-.593-.032a3.237,3.237,0,0,1-1.262-.2,3.274,3.274,0,0,1-.915-.9Z" transform="translate(2 3.499)" fill="#e6c000"/>
    <path id="Path_727" data-name="Path 727" d="M20.316,6.751l.274.783a4.562,4.562,0,0,0,.746,1.606,4.134,4.134,0,0,0,1.5.819l.733.305c2.835,1.179,4.252,1.769,4.413,2.975a2,2,0,0,1-.495,1.5c-.133-.036-.261-.065-.381-.09a20.941,20.941,0,0,0-2.816-.27l-.735-.04-.345-.02c-.062-.073-.134-.159-.227-.27l-.473-.566a21.192,21.192,0,0,0-1.908-2.1,3.785,3.785,0,0,0-3.155-1.089A3.733,3.733,0,0,0,14.8,12.342a20.429,20.429,0,0,0-1.092,2.59l-.256.687c-.043.116-.078.209-.108.289l-.289.114-.683.268a20.5,20.5,0,0,0-2.575,1.136,3.731,3.731,0,0,0-2.009,2.689,3.778,3.778,0,0,0,1.15,3.137,16.564,16.564,0,0,0,1.646,1.467c-2.353.638-3.6.876-4.315.109-.8-.864-.386-2.431.442-5.565l.214-.811a4.812,4.812,0,0,0,.294-1.778,4.579,4.579,0,0,0-.748-1.605l-.418-.706c-1.615-2.73-2.422-4.095-1.883-5.181s2.068-1.174,5.126-1.35l.791-.045a4.091,4.091,0,0,0,1.682-.277,4.494,4.494,0,0,0,1.22-1.271l.51-.649c1.973-2.508,2.96-3.763,4.094-3.569S19.255,3.725,20.316,6.751Z" transform="translate(0 0)" fill="#e6c000"/>
  </g>
</svg>

                </span>
                <h6>
                  Loyalty Points <span>2000</span>
                </h6>
              </div>
              <div className="amount-info">
                <h6>How to earn points</h6>
              </div>
              <div className="loyalty-earn-table">
                <div className="loyalty-earn-row loyalty-earn-head">
                  <span>Activity</span>
                  <span>Points</span>
                </div>
                {LOYALTY_ACTIVITIES.map((activity) => (
                  <div className="loyalty-earn-row" key={activity.activity}>
                    <span>{activity.activity}</span>
                    <span className="loyalty-points">{activity.points}</span>
                  </div>
                ))}
              </div>
            </div>
          </Tab>
          */}
        </Tabs>
      </div>
      {loading ? <SmallLoader /> : null}
      <GiftCardClaimOtp
        show={showOtp}
        giftCardCode={claimCode}
        onHide={() => setShowOtp(false)}
        onClaimed={handleClaimed}
      />
    </div>
  );
};

export default MyWallet;
